import React from "react";
import { Check, Clock, Package, Truck, MapPin, X, RotateCcw, IndianRupee } from "lucide-react";

const STEP_CONFIG = {
  placed: { label: "Order Placed", Icon: Clock },
  confirmed: { label: "Confirmed", Icon: Check },
  packed: { label: "Packed", Icon: Package },
  shipped: { label: "Shipped", Icon: Truck },
  out_for_delivery: { label: "Out for Delivery", Icon: Truck },
  delivered: { label: "Delivered", Icon: MapPin },
};

const TERMINAL = {
  cancelled: { label: "Cancelled", Icon: X, color: "text-destructive bg-destructive/10" },
  returned: { label: "Returned", Icon: RotateCcw, color: "text-amber-700 bg-amber-100" },
  refunded: { label: "Refunded", Icon: IndianRupee, color: "text-green-700 bg-green-100" },
  return_requested: { label: "Return Requested", Icon: RotateCcw, color: "text-amber-700 bg-amber-100" },
};

/**
 * Visual horizontal-to-vertical timeline showing order progress.
 *  status:       current status string
 *  steps:        ordered list of progress statuses (placed → delivered)
 *  timeline:     array of { status, at, note, by }
 */
const OrderTimeline = ({ status, steps = ["placed", "confirmed", "packed", "shipped", "out_for_delivery", "delivered"], timeline = [] }) => {
  // Map: status → first timestamp from the timeline (most recent transition wins)
  const stampByStatus = timeline.reduce((acc, t) => {
    if (!acc[t.status]) acc[t.status] = t;
    return acc;
  }, {});

  const currentIdx = steps.indexOf(status);
  const isTerminal = TERMINAL[status];
  const progressPct = currentIdx >= 0 ? (currentIdx / (steps.length - 1)) * 100 : 0;

  return (
    <div className="card-fah p-5" data-testid="order-timeline">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Order Progress</div>
          <div className="font-display text-lg font-bold mt-0.5">
            {isTerminal ? isTerminal.label : STEP_CONFIG[status]?.label || status}
          </div>
        </div>
        {isTerminal && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isTerminal.color}`}>
            {isTerminal.label}
          </span>
        )}
      </div>

      {/* Progress bar (horizontal, hidden on mobile - replaced by vertical below) */}
      {!isTerminal && (
        <div className="hidden md:block mt-2 mb-6">
          <div className="relative">
            <div className="absolute top-4 left-0 right-0 h-1 bg-muted rounded-full" />
            <div className="absolute top-4 left-0 h-1 bg-primary rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            <div className="relative flex justify-between">
              {steps.map((s, i) => {
                const cfg = STEP_CONFIG[s] || { label: s, Icon: Check };
                const done = i <= currentIdx;
                const active = i === currentIdx;
                return (
                  <div key={s} className="flex flex-col items-center w-1/6 text-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 z-10 transition-all ${
                      done ? "bg-primary border-primary text-white" : "bg-white border-muted text-muted-foreground"
                    } ${active ? "ring-4 ring-primary/20 scale-110" : ""}`}>
                      <cfg.Icon className="w-4 h-4" />
                    </div>
                    <div className={`mt-2 text-[11px] font-semibold leading-tight ${done ? "text-foreground" : "text-muted-foreground"}`}>{cfg.label}</div>
                    {stampByStatus[s] && <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(stampByStatus[s].at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Vertical timeline (mobile + always for history) */}
      <ol className="relative md:hidden border-l-2 border-muted ml-3 space-y-4 mt-2" data-testid="order-timeline-vertical">
        {steps.map((s, i) => {
          const cfg = STEP_CONFIG[s] || { label: s, Icon: Check };
          const done = i <= currentIdx;
          const active = i === currentIdx;
          const stamp = stampByStatus[s];
          return (
            <li key={s} className="ml-4 relative">
              <span className={`absolute -left-[26px] flex items-center justify-center w-6 h-6 rounded-full border-2 ${done ? "bg-primary border-primary text-white" : "bg-white border-muted text-muted-foreground"} ${active ? "ring-2 ring-primary/30" : ""}`}>
                <cfg.Icon className="w-3 h-3" />
              </span>
              <div className={`text-sm font-semibold ${done ? "text-foreground" : "text-muted-foreground"}`}>{cfg.label}</div>
              {stamp && <div className="text-xs text-muted-foreground">{new Date(stamp.at).toLocaleString("en-IN")}</div>}
              {stamp?.note && <div className="text-xs text-muted-foreground mt-0.5">{stamp.note}</div>}
            </li>
          );
        })}
      </ol>

      {/* Full history (any extra events like cancelled / return_requested) */}
      {timeline.length > 0 && (
        <details className="mt-4 group">
          <summary className="text-xs font-semibold text-primary cursor-pointer select-none">View full history ({timeline.length} events)</summary>
          <ol className="mt-3 space-y-1.5 text-xs">
            {[...timeline].reverse().map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <span><b className="text-foreground capitalize">{t.status.replace(/_/g, " ")}</b> · {new Date(t.at).toLocaleString("en-IN")}{t.by ? ` · by ${t.by}` : ""}{t.note ? ` — ${t.note}` : ""}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
};

export default OrderTimeline;
